import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    AI_LABELS, createAiModel, createAiProviderKey, deleteAiModel, deleteAiProviderKey, getAiMeta, getAiServiceModels,
    getAiProviderKeys, updateAiModel, updateAiProviderKey,
} from '../../Api';
import {
    AI_DATA_CLASSES, AI_MODEL_STATUSES, AI_PROVIDERS, AI_STATUSES, AiDataClass, AiMetaDto, AiModelDto, AiModelStatus,
    AiProvider, AiProviderKeyDto, AiStatus, AiUpsertModelRequest, AiUpsertProviderKeyRequest,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../../components/betupFeed/BetupFeedShared';
import {
    AiActionError, AiConfiguredChip, AiDataClassChip, AiErrorAlert, aiLoadErrorText, AiStatusChip, CredentialProperty,
    EnumSelect, formatAiDate, formatAiUsd, parseOptionalNumber, textError, toAiActionError,
} from '../../components/ai/AiShared';

// ─── Model dialog ────────────────────────────────────────────────────────────

interface ModelForm {
    provider: AiProvider;
    modelKey: string;
    inputPerMtok: string;
    outputPerMtok: string;
    cacheWritePerMtok: string;
    cacheReadPerMtok: string;
    status: AiModelStatus;
}

const num = (v: number | null | undefined) => (v == null ? '' : String(v));

function ModelDialog({ open, model, meta, onClose, onSaved }: {
    open: boolean;
    model: AiModelDto | null;
    meta: AiMetaDto | null;
    onClose: () => void;
    onSaved: (message: string) => void;
}) {
    const [form, setForm] = React.useState<ModelForm>({
        provider: 'ANTHROPIC', modelKey: '', inputPerMtok: '', outputPerMtok: '', cacheWritePerMtok: '', cacheReadPerMtok: '', status: 'ACTIVE',
    });
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setError(null);
        setForm(model ? {
            provider: model.provider, modelKey: model.modelKey, inputPerMtok: num(model.inputPerMtok), outputPerMtok: num(model.outputPerMtok),
            cacheWritePerMtok: num(model.cacheWritePerMtok), cacheReadPerMtok: num(model.cacheReadPerMtok), status: model.status,
        } : {
            provider: 'ANTHROPIC', modelKey: '', inputPerMtok: '', outputPerMtok: '', cacheWritePerMtok: '', cacheReadPerMtok: '', status: 'ACTIVE',
        });
    }, [open, model]);

    const save = async () => {
        const input = parseOptionalNumber(form.inputPerMtok);
        const output = parseOptionalNumber(form.outputPerMtok);
        const cacheWrite = parseOptionalNumber(form.cacheWritePerMtok);
        const cacheRead = parseOptionalNumber(form.cacheReadPerMtok);
        if (!form.modelKey.trim()) return setError(textError('Model key is required.'));
        if (input == null || output == null) return setError(textError('Input and output prices are required numbers.'));
        if (cacheWrite === undefined || cacheRead === undefined) return setError(textError('Cache prices must be numbers.'));
        const request: AiUpsertModelRequest = {
            provider: form.provider, modelKey: form.modelKey.trim(), inputPerMtok: input, outputPerMtok: output,
            cacheWritePerMtok: cacheWrite, cacheReadPerMtok: cacheRead, status: form.status,
        };
        setSaving(true);
        const result = model ? await updateAiModel(model.id, request) : await createAiModel(request);
        setSaving(false);
        if (result.error != null) return setError(toAiActionError(result, 'Save failed'));
        onSaved(`Model ${request.modelKey} saved.`);
    };

    const priceField = (key: 'inputPerMtok' | 'outputPerMtok' | 'cacheWritePerMtok' | 'cacheReadPerMtok', label: string, required = false) => (
        <TextField label={label} fullWidth required={required} value={form[key]} inputProps={{ inputMode: 'decimal' }}
            onChange={e => setForm(f => ({ ...f, [key]: e.target.value.replace(/[^\d.]/g, '') }))}
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }} color="text.secondary">$</Typography> }} />
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{model ? `Edit model ${model.modelKey}` : 'New model'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} sm={5}>
                        <EnumSelect label="Provider" value={form.provider} options={meta?.providers ?? AI_PROVIDERS}
                            onChange={v => v && setForm(f => ({ ...f, provider: v }))} />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <TextField label="Model key" fullWidth required value={form.modelKey} placeholder="claude-haiku-4-5"
                            onChange={e => setForm(f => ({ ...f, modelKey: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12}><Typography variant="caption" color="text.secondary">Prices in USD per 1M tokens.</Typography></Grid>
                    <Grid item xs={6}>{priceField('inputPerMtok', 'Input', true)}</Grid>
                    <Grid item xs={6}>{priceField('outputPerMtok', 'Output', true)}</Grid>
                    <Grid item xs={6}>{priceField('cacheWritePerMtok', 'Cache write')}</Grid>
                    <Grid item xs={6}>{priceField('cacheReadPerMtok', 'Cache read')}</Grid>
                    <Grid item xs={12} sm={6}>
                        <EnumSelect label="Status" value={form.status} options={meta?.modelStatuses ?? AI_MODEL_STATUSES}
                            onChange={v => v && setForm(f => ({ ...f, status: v }))} />
                    </Grid>
                </Grid>
                <AiErrorAlert error={error} onClose={() => setError(null)} sx={{ mt: 2 }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Provider key dialog ─────────────────────────────────────────────────────

interface KeyForm {
    provider: AiProvider;
    name: string;
    baseUrl: string;
    workspaceRef: string;
    maxDataClass: AiDataClass;
    priority: string;
    status: AiStatus;
}

const KEY_NAME_PATTERN = /^[a-z0-9-]+$/;

export function credentialPropertyFor(pattern: string | undefined, provider: string, name: string): string {
    const base = pattern || 'betup.ai.credentials.<provider>.<name>';
    return base.replace('<provider>', provider.toLowerCase()).replace('<name>', name || '<name>');
}

function ProviderKeyDialog({ open, providerKey, meta, onClose, onSaved }: {
    open: boolean;
    providerKey: AiProviderKeyDto | null;
    meta: AiMetaDto | null;
    onClose: () => void;
    onSaved: (message: string) => void;
}) {
    const empty: KeyForm = { provider: 'ANTHROPIC', name: '', baseUrl: '', workspaceRef: '', maxDataClass: 'INTERNAL', priority: '100', status: 'ACTIVE' };
    const [form, setForm] = React.useState<KeyForm>(empty);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setError(null);
        setForm(providerKey ? {
            provider: providerKey.provider, name: providerKey.name, baseUrl: providerKey.baseUrl ?? '', workspaceRef: providerKey.workspaceRef ?? '',
            maxDataClass: providerKey.maxDataClass, priority: String(providerKey.priority), status: providerKey.status,
        } : empty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, providerKey]);

    const save = async () => {
        if (!KEY_NAME_PATTERN.test(form.name)) return setError(textError('Name: lowercase letters, digits, hyphens.'));
        const priority = parseOptionalNumber(form.priority);
        if (priority == null) return setError(textError('Priority is a required number.'));
        const request: AiUpsertProviderKeyRequest = {
            provider: form.provider, name: form.name, baseUrl: form.baseUrl.trim() || null, workspaceRef: form.workspaceRef.trim() || null,
            maxDataClass: form.maxDataClass, priority, status: form.status,
        };
        setSaving(true);
        const result = providerKey ? await updateAiProviderKey(providerKey.id, request) : await createAiProviderKey(request);
        setSaving(false);
        if (result.error != null) return setError(toAiActionError(result, 'Save failed'));
        onSaved(`Provider key ${request.provider.toLowerCase()}/${request.name} saved.`);
    };

    const property = providerKey?.credentialProperty ?? credentialPropertyFor(meta?.credentialPropertyPattern, form.provider, form.name);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{providerKey ? `Edit provider key ${providerKey.provider.toLowerCase()}/${providerKey.name}` : 'New provider key'}</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Only metadata is stored here. The secret value lives in configuration under
                    <Box sx={{ mt: 0.5 }}><CredentialProperty property={property} /></Box>
                </Alert>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                        <EnumSelect label="Provider" value={form.provider} options={meta?.providers ?? AI_PROVIDERS}
                            onChange={v => v && setForm(f => ({ ...f, provider: v }))} />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <TextField label="Name" fullWidth required value={form.name} placeholder="default"
                            error={form.name !== '' && !KEY_NAME_PATTERN.test(form.name)} helperText="lowercase letters, digits, hyphens"
                            onChange={e => setForm(f => ({ ...f, name: e.target.value.toLowerCase() }))} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Base URL (optional)" fullWidth value={form.baseUrl} onChange={e => setForm(f => ({ ...f, baseUrl: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Workspace ref (optional)" fullWidth value={form.workspaceRef} onChange={e => setForm(f => ({ ...f, workspaceRef: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <EnumSelect label="Max data class" value={form.maxDataClass} options={meta?.dataClasses ?? AI_DATA_CLASSES}
                            onChange={v => v && setForm(f => ({ ...f, maxDataClass: v }))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Priority" fullWidth value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value.replace(/[^\d-]/g, '') }))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <EnumSelect label="Status" value={form.status} options={meta?.statuses ?? AI_STATUSES}
                            onChange={v => v && setForm(f => ({ ...f, status: v }))} />
                    </Grid>
                </Grid>
                <AiErrorAlert error={error} onClose={() => setError(null)} sx={{ mt: 2 }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────────

type PendingDelete = { kind: 'model'; item: AiModelDto } | { kind: 'key'; item: AiProviderKeyDto } | null;

export default function AiModelsPage() {
    const [meta, setMeta] = React.useState<AiMetaDto | null>(null);
    const [models, setModels] = React.useState<AiModelDto[]>([]);
    const [keys, setKeys] = React.useState<AiProviderKeyDto[]>([]);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [modelDialog, setModelDialog] = React.useState<{ open: boolean; model: AiModelDto | null }>({ open: false, model: null });
    const [keyDialog, setKeyDialog] = React.useState<{ open: boolean; key: AiProviderKeyDto | null }>({ open: false, key: null });
    const [pendingDelete, setPendingDelete] = React.useState<PendingDelete>(null);

    const load = React.useCallback(async () => {
        const [m, ms, ks] = await Promise.all([getAiMeta(), getAiServiceModels(), getAiProviderKeys()]);
        setMeta(m);
        setModels(ms ?? []);
        setKeys(ks ?? []);
        setLoadError(ms == null ? aiLoadErrorText(AI_LABELS.models, 'Failed to load models.')
            : ks == null ? aiLoadErrorText(AI_LABELS.providerKeys, 'Failed to load provider keys.') : null);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const onSaved = (message: string) => {
        setModelDialog({ open: false, model: null });
        setKeyDialog({ open: false, key: null });
        setNotice(message);
        setError(null);
        load();
    };

    const remove = async () => {
        if (pendingDelete == null) return;
        const target = pendingDelete;
        setPendingDelete(null);
        const result = target.kind === 'model' ? await deleteAiModel(target.item.id) : await deleteAiProviderKey(target.item.id);
        if (result.error != null) {
            setError(toAiActionError(result, 'Delete failed'));
            return;
        }
        setNotice(target.kind === 'model' ? `Model ${target.item.modelKey} deleted.` : `Provider key ${target.item.name} deleted.`);
        load();
    };

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">AI models & provider keys</Typography>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            <AiErrorAlert error={error} onClose={() => setError(null)} />
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Box>
                            <Typography variant="h6">Models</Typography>
                            <Typography variant="body2" color="text.secondary">Prices (USD per 1M tokens) are used for cost accounting. A model used by tasks/agents cannot be deleted (409).</Typography>
                        </Box>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModelDialog({ open: true, model: null })}>New model</Button>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Model key</TableCell>
                                <TableCell>Provider</TableCell>
                                <TableCell align="right">Input</TableCell>
                                <TableCell align="right">Output</TableCell>
                                <TableCell align="right">Cache write</TableCell>
                                <TableCell align="right">Cache read</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Updated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {models.length === 0 && <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">No models.</Typography></TableCell></TableRow>}
                            {models.map(m => (
                                <TableRow key={m.id}>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{m.modelKey}</TableCell>
                                    <TableCell>{m.provider}</TableCell>
                                    <TableCell align="right">{formatAiUsd(m.inputPerMtok)}</TableCell>
                                    <TableCell align="right">{formatAiUsd(m.outputPerMtok)}</TableCell>
                                    <TableCell align="right">{formatAiUsd(m.cacheWritePerMtok)}</TableCell>
                                    <TableCell align="right">{formatAiUsd(m.cacheReadPerMtok)}</TableCell>
                                    <TableCell><AiStatusChip status={m.status} /></TableCell>
                                    <TableCell><Typography variant="caption">{formatAiDate(m.updatedAt)}</Typography></TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Edit"><IconButton size="small" onClick={() => setModelDialog({ open: true, model: m })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setPendingDelete({ kind: 'model', item: m })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Box>
                            <Typography variant="h6">Provider keys</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Metadata only — set the secret in configuration under the credential property. Keys are chosen by priority among those allowed to receive the data class.
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setKeyDialog({ open: true, key: null })}>New provider key</Button>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Key</TableCell>
                                <TableCell>Credential property</TableCell>
                                <TableCell>Max data class</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last used / OK</TableCell>
                                <TableCell>Last error</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {keys.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No provider keys.</Typography></TableCell></TableRow>}
                            {keys.map(k => (
                                <TableRow key={k.id} sx={{ verticalAlign: 'top' }}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{k.provider.toLowerCase()} / {k.name}</Typography>
                                        {k.baseUrl && <Typography variant="caption" color="text.secondary" display="block">{k.baseUrl}</Typography>}
                                        {k.workspaceRef && <Typography variant="caption" color="text.secondary" display="block">workspace {k.workspaceRef}</Typography>}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 340 }}>
                                        <CredentialProperty property={k.credentialProperty} />
                                        <AiConfiguredChip configured={k.configured} />
                                    </TableCell>
                                    <TableCell><AiDataClassChip dataClass={k.maxDataClass} /></TableCell>
                                    <TableCell>{k.priority}</TableCell>
                                    <TableCell><AiStatusChip status={k.status} /></TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">used {formatAiDate(k.lastUsedAt)}</Typography>
                                        <Typography variant="caption" display="block" color="success.main">ok {formatAiDate(k.lastOkAt)}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 280 }}>
                                        {k.lastError ? (
                                            <>
                                                <Typography variant="caption" color="error" display="block" sx={{ wordBreak: 'break-word' }}>{k.lastError}</Typography>
                                                <Typography variant="caption" color="text.secondary">{formatAiDate(k.lastErrorAt)}</Typography>
                                            </>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Edit"><IconButton size="small" onClick={() => setKeyDialog({ open: true, key: k })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setPendingDelete({ kind: 'key', item: k })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <ModelDialog open={modelDialog.open} model={modelDialog.model} meta={meta}
                onClose={() => setModelDialog({ open: false, model: null })} onSaved={onSaved} />
            <ProviderKeyDialog open={keyDialog.open} providerKey={keyDialog.key} meta={meta}
                onClose={() => setKeyDialog({ open: false, key: null })} onSaved={onSaved} />
            <ConfirmDialog
                open={pendingDelete != null}
                title={pendingDelete?.kind === 'model' ? 'Delete model?' : 'Delete provider key?'}
                text={pendingDelete == null ? '' : pendingDelete.kind === 'model'
                    ? <>Model <b>{pendingDelete.item.modelKey}</b> will be deleted. Fails (409) while tasks or agents use it — set it RETIRED instead.</>
                    : <>Provider key <b>{pendingDelete.item.provider.toLowerCase()}/{pendingDelete.item.name}</b> will be deleted. Calls pinned to it will fail with NO_PROVIDER_KEY.</>}
                confirmLabel="Delete"
                confirmColor="error"
                onClose={() => setPendingDelete(null)}
                onConfirm={remove}
            />
        </Stack>
    );
}
